import type { Editor } from "@tiptap/core"
import type { Node as PMNode, ResolvedPos } from "@tiptap/pm/model"

/**
 * Converts the user's current pos (indicated by their mouse pointer or selection)
 * into the most reasonable $addTargetPos.
 *
 * The $addTargetPos is a position near userPos (generally before it) that is guaranteed to point to
 * a "targetable" node, which means a node in groups "block" or "addTarget".
 * Specifically, it is the node's first inner position (content start), except for widgets,
 * where it is the position of the node itself.
 *
 * This position is the basis for computing where to add content. It is not
 * literally any of these positions, just a useful intermediate state.
 */
export function findAddTargetPos(editor: Editor, userPos: number): ResolvedPos {
  // The code below doesn't work for size-1 widgets because the pos can't be
  // inside them. Check this special case here.
  // Empirically, the mouse position can resolve to the widget's pos or the next pos,
  // depending on which vertical half of the widget you mouse over.
  if (isTargetableWidget(editor, userPos)) return editor.state.doc.resolve(userPos)
  if (isTargetableWidget(editor, userPos - 1)) return editor.state.doc.resolve(userPos - 1)

  // Find the add target position.
  // Do it by walking backwards until we are at the start of a targetable node.
  let $addTargetPos = editor.state.doc.resolve(userPos)
  // This loop will terminate because we always move backwards if we don't break.
  while ($addTargetPos.pos !== 0) {
    $addTargetPos = backToTargetableAncOrDoc($addTargetPos)
    if ($addTargetPos.parentOffset === 0) {
      break
    } else {
      $addTargetPos = editor.state.doc.resolve($addTargetPos.pos - 1)
    }
  }

  if ($addTargetPos.pos === 0) {
    // The first opportunity to show an Add button is on the first child
    // of the first card, which is the second child of doc (after the lesson title).
    // Move from doc-start to there.
    // TODO: May need to create the card.
    $addTargetPos = editor.state.doc.resolve(editor.state.doc.child(0).nodeSize + 2)
  }

  return $addTargetPos
}

export function isTargetableWidget(editor: Editor, pos: number): boolean {
  if (pos < 0 || pos > editor.state.doc.content.size) return false
  const maybeWidget = editor.state.doc.nodeAt(pos)
  return !!maybeWidget && maybeWidget.nodeSize === 1 && isTargetable(maybeWidget)
}

/**
 * Moves $pos backwards until it is directly inside a targetable node or the doc.
 */
function backToTargetableAncOrDoc($pos: ResolvedPos): ResolvedPos {
  let ancDepth = 0
  for (ancDepth = $pos.depth; ancDepth >= 0; ancDepth--) {
    const node = $pos.node(ancDepth)
    if (isTargetable(node) || node.type.name === "doc") {
      return $pos.doc.resolve($pos.posAtIndex($pos.index(ancDepth), ancDepth))
    }
  }

  // Unreachable
  return $pos.doc.resolve(0)
}

function isTargetable(node: PMNode): boolean {
  // A node is targetable if it is a (last-level) block or explicitly opts in.
  return node.type.isInGroup("block") || node.type.isInGroup("addTarget")
}

/**
 * Finds the depth of the deepest card containing $pos,
 * or null if none contain it.
 */
export function findCardDepth($pos: ResolvedPos): number | null {
  for (let i = $pos.depth; i >= 0; i--) {
    if ($pos.node(i).type.isInGroup("card")) return i
  }
  return null
}
